import { ReactNode, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { GemIconDefs } from "@/components/GemIcon";
import { ThemeProvider } from "next-themes";
import { useIsPageVisible } from "@/hooks/use-page-visibility";
import Index from "./pages/Index";

const About = lazy(() => import("./pages/About"));
const Pharmacies = lazy(() => import("./pages/Pharmacies"));
const Physicians = lazy(() => import("./pages/Physicians"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SignIn = lazy(() => import("./pages/SignIn"));
const Services = lazy(() => import("./pages/Services"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const PharmacyDemo = lazy(() => import("./pages/PharmacyDemo"));
const ProviderDemo = lazy(() => import("./pages/ProviderDemo"));
const AdminDemo = lazy(() => import("./pages/AdminDemo"));
const AdminOverviewSnapshot = lazy(() => import("./pages/AdminOverviewSnapshot"));
const EnrollmentDemo = lazy(() => import("./pages/EnrollmentDemo"));
const PreConsultation = lazy(() => import("./pages/PreConsultation"));
const SeoOverview = lazy(() => import("./pages/SeoOverview"));
const Security = lazy(() => import("./pages/Security"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Checkout = lazy(() => import("./pages/Checkout"));
const ResourceHub = lazy(() => import("./pages/resources/ResourceHub"));
const ResourcePillar = lazy(() => import("./pages/resources/ResourcePillar"));
const ResourceArticle = lazy(() => import("./pages/resources/ResourceArticle"));

const queryClient = new QueryClient();

const VisibilityGate = ({ path, children }: { path: string; children: ReactNode }) => {
  const { isVisible, isLoading, isError } = useIsPageVisible(path);

  if (isLoading) return <div className="min-h-screen bg-background" />;
  if (!isError && !isVisible) return <NotFound />;

  return <>{children}</>;
};

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <GemIconDefs />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              {/* Public pages — no VisibilityGate so crawlers index immediately */}
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/industries" element={<Services />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/pharmacies" element={<Pharmacies />} />
              <Route path="/physicians" element={<Physicians />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/security" element={<Security />} />
              <Route path="/checkout" element={<Checkout />} />

              {/* Resource Center */}
              <Route path="/resources" element={<ResourceHub />} />
              <Route path="/pharmacy-growth-guide" element={<ResourcePillar cluster="pharmacy-growth" />} />
              <Route path="/pharmacy-growth/:slug" element={<ResourceArticle cluster="pharmacy-growth" />} />
              <Route path="/pharmacy-software" element={<ResourcePillar cluster="pharmacy-software" />} />
              <Route path="/pharmacy-software/:slug" element={<ResourceArticle cluster="pharmacy-software" />} />
              <Route path="/provider-portal" element={<ResourcePillar cluster="provider-portal" />} />
              <Route path="/provider-portal/:slug" element={<ResourceArticle cluster="provider-portal" />} />

              {/* Internal / demo pages — gated */}
              <Route path="/pharmacy-demo" element={<VisibilityGate path="/pharmacy-demo"><PharmacyDemo /></VisibilityGate>} />
              <Route path="/provider/demo-dashboard" element={<VisibilityGate path="/provider/demo-dashboard"><ProviderDemo /></VisibilityGate>} />
              <Route path="/admin/demo-dashboard" element={<VisibilityGate path="/admin/demo-dashboard"><AdminDemo /></VisibilityGate>} />
              <Route path="/admin/overview-snapshot" element={<VisibilityGate path="/admin/overview-snapshot"><AdminOverviewSnapshot /></VisibilityGate>} />
              <Route path="/enrollment/demo" element={<VisibilityGate path="/enrollment/demo"><EnrollmentDemo /></VisibilityGate>} />
              <Route path="/pre-consultation" element={<VisibilityGate path="/pre-consultation"><PreConsultation /></VisibilityGate>} />
              <Route path="/seo" element={<SeoOverview />} />
              <Route path="/signin" element={<VisibilityGate path="/signin"><SignIn /></VisibilityGate>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;

